import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { handleError } from '@risksmart-app/components/src/utils/errorUtils';
import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import superjson from 'superjson';

import {
  getQueryClient,
  TRPCProvider as TanstackTRPCProvider,
} from '../utils/trpc';
import { ThirdPartyAuth0Context } from './ThirdPartyAuth0Context';

interface Props {
  children: ReactNode;
}

/**
 * tRPC v3 provider for the portal (RSP-6570), mirroring
 * `packages/web/src/providers/TrpcProvider.tsx`. Reaches the tRPC API at
 * `REACT_APP_TRPC_API_URL` + `/trpc` — its own var, exactly as web has, NOT
 * `REACT_APP_TENANT_API_URL`. The two coincide only in deployed environments:
 * locally the tenant var points at graphql-engine (`:8080`) while tRPC listens
 * on `:2021`, and on ephemeral the tenant var carries a `/hasura` prefix. Deriving
 * the tRPC URL from the Hasura one made the flag impossible to exercise outside
 * a deployed env and removed the ability to point portal tRPC at a different
 * host from Hasura. Authorises with a Bearer token from the portal's
 * `ThirdPartyAuth0Context`.
 * Mounted unconditionally alongside Apollo so the flag can flip per environment
 * without any provider-tree change; only the hook twins branch on the flag.
 */
export const TrpcProvider: FC<Props> = ({ children }) => {
  const { getAccessTokenSilently, isLoading, isAuthenticated } =
    useRisksmartUser(ThirdPartyAuth0Context);

  const queryClient = getQueryClient();

  // Use refs so the headers function always reads current values
  // without recreating the tRPC client (which causes issues with React Query)
  const getTokenRef = useRef(getAccessTokenSilently);
  const isLoadingRef = useRef(isLoading);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently;
    isLoadingRef.current = isLoading;
    isAuthenticatedRef.current = isAuthenticated;
  }, [getAccessTokenSilently, isLoading, isAuthenticated]);

  // Clear cache when auth state changes to prevent stale data from previous sessions
  useEffect(() => {
    if (!isLoading) {
      queryClient.clear();
    }
  }, [isAuthenticated, queryClient, isLoading]);

  const [trpcClient] = useState(() => {
    return createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url: `${getEnv('REACT_APP_TRPC_API_URL')}/trpc`,
          maxItems: 1,
          transformer: superjson,
          async headers() {
            if (isLoadingRef.current || !isAuthenticatedRef.current) {
              return {};
            }

            try {
              const token = await getTokenRef.current();

              return { Authorization: `Bearer ${token}` };
            } catch (error) {
              handleError(
                error instanceof Error
                  ? error
                  : 'tRPC: Failed to get access token'
              );

              return {};
            }
          },
        }),
      ],
    });
  });

  return (
    <QueryClientProvider client={queryClient}>
      <TanstackTRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TanstackTRPCProvider>
    </QueryClientProvider>
  );
};
